import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect } from "react";
import { authenticatedApi } from "@/lib/api";
import type { AlwaysAiUser } from "../../../shared/api-types";
import {
  applyAlwaysTheme,
  decideAlwaysTheme,
  readThemeCacheOwner,
  updateThemeCache,
  type AlwaysThemeDecision,
} from "./always-theme";
import { browserStorage, resolveLook, useShellLook } from "./look-preference";
import { ALWAYS_BRAND_FLAG, RD_HOME_FLAG, useFeatures } from "./use-features";

// The viewer, flags and look the workspace frame reads, on the same query keys,
// so a workspace page makes no extra request for the theme.
function useThemeDecision(): {
  decision: AlwaysThemeDecision | null;
  clerkId: string | null;
} {
  const { getToken, isLoaded, isSignedIn, userId } = useAuth({
    treatPendingAsSignedOut: false,
  });
  const signedIn = isLoaded && isSignedIn === true;
  const me = useQuery({
    queryKey: ["current-user"],
    enabled: signedIn,
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });
  const features = useFeatures();
  const shellLook = useShellLook(userId);
  const flagOn = features.data?.data[ALWAYS_BRAND_FLAG] === true;
  const clerkId = userId ?? null;
  const decision = decideAlwaysTheme({
    signedIn,
    clerkId,
    featuresPending: !features.isSuccess && !features.isError,
    flagOn,
    mePending: !me.isSuccess && !me.isError,
    user: me.data?.user,
    rdHomeEnabled: features.data?.data[RD_HOME_FLAG] === true,
    look: resolveLook({ flagOn, stored: shellLook.stored }),
    lastLook: shellLook.lastLook,
    cachedOwner: readThemeCacheOwner(browserStorage()),
  });

  return { decision, clerkId };
}

/**
 * Keeps data-theme="always" on <html> in step with the rendered frame
 * (NO-790; see lib/always-theme.ts). Mounted once, by the root route.
 */
export function useAlwaysThemeSync(): void {
  const { decision, clerkId } = useThemeDecision();
  // Null while signed out: the first-paint attribute and the cache stay as they are.
  const active = decision !== null;
  const on = decision?.on ?? false;
  const cache = decision?.cache ?? "keep";

  // A layout effect, so a look switch or a settled frame paints already themed.
  useLayoutEffect(() => {
    if (!active || clerkId === null) return;

    applyAlwaysTheme(document.documentElement, on);
    updateThemeCache(browserStorage(), cache, clerkId);
  }, [active, on, cache, clerkId]);
}
